import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Upload, TrendingDown, TrendingUp, Users, Wallet, AlertTriangle, Clock, UserMinus,
  Trash2, Download, ShieldCheck, CalendarDays, Loader2, Info, Percent, HeartPulse,
  Building2, ArrowRight, Coins, Activity, ShieldAlert, MapPin, Megaphone, Layers, Target, Receipt,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useGet } from '@/lib/useData';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendAreaChart } from '@/components/charts/TrendAreaChart';
import { LabeledBars } from '@/components/charts/LabeledBars';
import { HorizontalBars } from '@/components/charts/HorizontalBars';
import { RemboursementsSection } from '@/components/attrition/RemboursementsSection';

const C_ACTIF = '#10B981';
const C_ARRET = '#EF4444';
const C_SUSPENDU = '#F59E0B';
const C_DARK = '#7C3AED';

const fmtInt = (n) => (n ?? 0).toLocaleString('fr-FR');
const fmtEUR = (n) => `${Math.round(n ?? 0).toLocaleString('fr-FR')} €`;
const fmtPct = (n) => `${(n ?? 0).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`;
const fmt1 = (n) => (n ?? 0).toLocaleString('fr-FR', { maximumFractionDigits: 1 });

const MONTHS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
const fmtReportDate = (iso) => {
  if (!iso) return '—';
  const [y, mo, d] = String(iso).split('-');
  return `${parseInt(d, 10)} ${MONTHS[parseInt(mo, 10) - 1] || ''} ${y}`;
};
const fmtMonthShort = (iso) => {
  if (!iso) return '';
  const [y, mo] = String(iso).split('-');
  return `${MONTHS[parseInt(mo, 10) - 1] || ''} ${String(y).slice(2)}`;
};

// --------------------------------------------------------------------------- //
const StatCard = ({ icon: Icon, label, value, sub, accent, delta, deltaGoodWhenDown, testid }) => {
  let deltaEl = null;
  if (delta !== null && delta !== undefined && Number.isFinite(delta) && delta !== 0) {
    const up = delta > 0;
    const good = deltaGoodWhenDown ? !up : up;
    const DIcon = up ? TrendingUp : TrendingDown;
    deltaEl = (
      <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold"
        style={{ color: good ? C_ACTIF : C_ARRET }} data-testid={`${testid}-delta`}>
        <DIcon className="h-3 w-3" />{up ? '+' : ''}{fmt1(delta)}
      </span>
    );
  }
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="mb-2 flex items-center justify-between">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: `${accent}1A`, color: accent }}>
          <Icon className="h-4.5 w-4.5" />
        </span>
        {deltaEl}
      </div>
      <div className="font-display text-2xl font-bold leading-none text-foreground" data-testid={`${testid}-value`}>{value}</div>
      <div className="mt-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      {sub && <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>}
    </Card>
  );
};

const SegBar = ({ segments, testid }) => {
  const total = Math.max(1, segments.reduce((s, x) => s + (x.v || 0), 0));
  return (
    <div>
      <div className="flex h-3.5 w-full overflow-hidden rounded-full bg-muted" data-testid={testid}>
        {segments.map((s) => s.v > 0 && (
          <div key={s.k} style={{ width: `${(s.v / total) * 100}%`, background: s.c }} className="h-full" title={`${s.k}: ${s.v}`} />
        ))}
      </div>
      <div className="mt-3 grid gap-2" style={{ gridTemplateColumns: `repeat(${segments.length}, minmax(0,1fr))` }}>
        {segments.map((s) => (
          <div key={s.k} className="rounded-lg border p-2 text-center">
            <div className="mx-auto mb-1 h-2 w-2 rounded-full" style={{ background: s.c }} />
            <div className="font-display text-sm font-bold">{fmtInt(s.v)}</div>
            <div className="text-[10px] text-muted-foreground">{s.k}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Barre de taux colorée (verte/ambre/rouge) pour les tableaux de segments.
const RateBar = ({ taux, tone }) => (
  <div className="flex items-center gap-2">
    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
      <div className="h-full rounded-full" style={{ width: `${Math.min(100, taux)}%`, background: tone }} />
    </div>
    <span className="w-12 text-right text-xs font-semibold" style={{ color: tone }}>{fmtPct(taux)}</span>
  </div>
);

const UploadZone = ({ onFile, busy, compact }) => {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const handleFiles = (files) => {
    const f = files?.[0];
    if (!f) return;
    if (!/\.(xlsx|xls)$/i.test(f.name)) { toast.error('Format attendu : fichier Excel (.xlsx)'); return; }
    onFile(f);
  };
  return (
    <div
      role="button" tabIndex={0}
      onClick={() => !busy && inputRef.current?.click()}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !busy) inputRef.current?.click(); }}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); if (!busy) handleFiles(e.dataTransfer.files); }}
      className={`group flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed text-center transition-colors duration-200 ${compact ? 'gap-1.5 p-4' : 'gap-2.5 p-9'} ${drag ? 'accent-bg-soft' : 'hover:accent-border'} ${busy ? 'pointer-events-none opacity-70' : ''}`}
      style={{ borderColor: drag ? 'var(--tenant-accent-500)' : undefined }}
      data-testid="attr-upload-zone"
    >
      <input ref={inputRef} type="file" accept=".xlsx,.xls" className="hidden"
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }} data-testid="attr-upload-input" />
      <span className={`flex items-center justify-center rounded-2xl ${compact ? 'h-9 w-9' : 'h-12 w-12'}`}
        style={{ background: 'var(--tenant-accent-050)', color: 'var(--tenant-accent-600)' }}>
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
      </span>
      <div className={`font-display font-semibold ${compact ? 'text-sm' : 'text-base'}`}>
        {busy ? 'Analyse du fichier…' : 'Déposez un rapport Excel'}
      </div>
      {!compact && (
        <p className="max-w-sm text-xs text-muted-foreground">
          Glissez-déposez le fichier d'attrition (.xlsx) envoyé par l'association, ou cliquez pour le sélectionner.
        </p>
      )}
    </div>
  );
};

const SectionTitle = ({ icon: Icon, title, desc, right }) => (
  <div className="mb-3 flex items-start justify-between gap-2">
    <div>
      <h3 className="flex items-center gap-2 font-display text-base font-semibold">
        {Icon && <Icon className="h-4 w-4 accent-text" />} {title}
      </h3>
      {desc && <p className="mt-0.5 text-xs text-muted-foreground">{desc}</p>}
    </div>
    {right}
  </div>
);

// --------------------------------------------------------------------------- //
export default function Attrition() {
  const [association, setAssociation] = useState('Amnesty International');
  const [selId, setSelId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [onlyRisk, setOnlyRisk] = useState(false);
  const [cpa, setCpa] = useState(() => localStorage.getItem('attr_cpa') || '');

  useEffect(() => { localStorage.setItem('attr_cpa', cpa); }, [cpa]);

  const reportsQ = useGet('/attrition/reports', association ? { association } : undefined, true);
  const evoQ = useGet('/attrition/evolution', association ? { association } : undefined, true);
  const reports = reportsQ.data || [];
  const evolution = evoQ.data || [];

  useEffect(() => {
    if (reports.length && !reports.find((r) => r.id === selId)) setSelId(reports[0].id);
    if (!reports.length) setSelId(null);
  }, [reports, selId]);

  const detailQ = useGet(selId ? `/attrition/reports/${selId}` : null, undefined, !!selId);
  const detail = detailQ.data;

  const associations = useMemo(() => {
    const set = new Set(['Amnesty International']);
    reports.forEach((r) => r.association && set.add(r.association));
    return Array.from(set);
  }, [reports]);

  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('association', association || 'Amnesty International');
      const { data } = await api.post('/attrition/reports', fd);
      toast.success(`Rapport importé — ${fmtReportDate(data?.report_date)} · attrition ${fmtPct(data?.metrics?.taux_attrition)}`);
      await Promise.all([reportsQ.refetch(), evoQ.refetch()]);
      if (data?.id) setSelId(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import impossible — vérifiez le format du fichier.");
    } finally { setBusy(false); }
  };

  const removeReport = async (id) => {
    try {
      await api.delete(`/attrition/reports/${id}`);
      toast.success('Rapport supprimé');
      await Promise.all([reportsQ.refetch(), evoQ.refetch()]);
      setSelId(null);
    } catch { toast.error('Suppression impossible'); }
  };

  const downloadReport = async (id, filename) => {
    try {
      const { data } = await api.get(`/attrition/reports/${id}/download`, { responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(data);
      a.download = filename || 'rapport.xlsx';
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error('Téléchargement impossible'); }
  };

  const m = detail?.metrics || {};
  const avg = m.taux_attrition || 0;
  const riskCount = (detail?.recruteurs_a_risque || []).length;
  const rateTone = (t) => (t >= 60 || t >= avg + 15 ? C_ARRET : (t >= 50 || t >= avg + 8 ? C_SUSPENDU : C_ACTIF));

  const evoIdx = evolution.findIndex((p) => p.report_date === detail?.report_date);
  const prevPoint = evoIdx > 0 ? evolution[evoIdx - 1] : null;
  const attritionDelta = prevPoint ? m.taux_attrition - prevPoint.taux_attrition : null;
  const actifsDelta = prevPoint ? m.donateurs_actifs - prevPoint.donateurs_actifs : null;

  const evoData = evolution.map((p) => ({ period: p.report_date, attrition: p.taux_attrition, precoce: p.taux_attrition_precoce }));
  const echData = (detail?.echeance_order || []).map((k) => ({ period: k, value: (detail?.echeance_buckets || {})[k] || 0 }));
  const survData = (detail?.survival || []).map((s) => ({ period: String(s.echeance), retention: s.retention }));
  const motifData = (detail?.by_motif || []).slice(0, 10).map((x) => ({
    key: x.motif.length > 32 ? `${x.motif.slice(0, 32)}…` : x.motif, value: x.count, pct: x.pct,
  }));

  // Rentabilité (LTV vs coût d'acquisition saisi par l'utilisateur).
  const cpaNum = parseFloat(String(cpa).replace(',', '.')) || 0;
  const montantMoy = m.montant_moyen_actif || 0;
  const revMoy = m.revenu_moyen_donateur || 0;
  const survMap = Object.fromEntries((detail?.survival || []).map((s) => [s.echeance, s.retention]));
  const breakevenEch = cpaNum > 0 && montantMoy > 0 ? Math.ceil(cpaNum / montantMoy) : null;
  const pctProfitable = breakevenEch != null ? (survMap[Math.min(breakevenEch, 12)] ?? 0) : null;
  const roiRatio = cpaNum > 0 ? revMoy / cpaNum : null;

  const loading = reportsQ.isLoading;
  const hasReports = reports.length > 0;
  const isAmnesty = /amnesty/i.test(association || '');
  const rej = detail?.rejets || {};

  return (
    <TooltipProvider delayDuration={150}>
      <div className="mx-auto max-w-[1400px] space-y-6 px-4 py-6 sm:px-6" data-testid="attrition-page">
        {/* HERO */}
        <div className="relative overflow-hidden rounded-3xl border p-6 text-white sm:p-8"
          style={{ background: 'radial-gradient(900px 520px at 18% 10%, rgba(238,114,46,0.22), transparent 55%), radial-gradient(700px 520px at 85% 0%, rgba(59,130,246,0.16), transparent 60%), linear-gradient(160deg, #0B1220 0%, #111B33 55%, #0A1020 100%)' }}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <Badge variant="outline" className="mb-2 border-white/25 bg-white/10 text-[10px] uppercase tracking-wider text-white/80">
                <ShieldCheck className="mr-1 h-3 w-3" /> Confidentiel · Direction
              </Badge>
              <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Attrition</h1>
              <p className="mt-1.5 max-w-xl text-sm text-white/70">
                Rétention des donateurs, rentabilité du recrutement et qualité par recruteur, canal et campagne — à partir des rapports mensuels des associations.
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-2 rounded-2xl border border-white/15 bg-white/10 px-2 py-1.5 backdrop-blur">
                <Building2 className="ml-1 h-4 w-4 text-white/70" />
                <Select value={association} onValueChange={setAssociation}>
                  <SelectTrigger className="h-8 min-w-[190px] border-0 bg-transparent text-sm font-semibold text-white shadow-none focus:ring-0" data-testid="attr-assoc-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>{associations.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {hasReports && <span className="text-[11px] text-white/60">{reports.length} rapport{reports.length > 1 ? 's' : ''} · dernier : {fmtReportDate(reports[0]?.report_date)}</span>}
            </div>
          </div>
        </div>

        {loading && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
        )}

        {!loading && !hasReports && (
          <Card className="p-8" data-testid="attr-empty">
            <div className="mx-auto max-w-lg text-center">
              <h2 className="font-display text-xl font-bold">Aucun rapport pour {association}</h2>
              <p className="mt-1 text-sm text-muted-foreground">Importez le premier fichier Excel d'attrition pour démarrer le suivi.</p>
            </div>
            <div className="mx-auto mt-5 max-w-xl"><UploadZone onFile={upload} busy={busy} /></div>
          </Card>
        )}

        {!loading && hasReports && (
          <>
            {/* Barre sélection + upload */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Rapport :</span>
                <Select value={selId || ''} onValueChange={setSelId}>
                  <SelectTrigger className="h-9 min-w-[190px] rounded-xl font-semibold" data-testid="attr-report-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {reports.map((r) => (
                      <SelectItem key={r.id} value={r.id} data-testid={`attr-report-opt-${r.report_date}`}>
                        {fmtReportDate(r.report_date)} · {fmtPct(r.metrics?.taux_attrition)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {detail && (
                  <>
                    <Button variant="outline" size="sm" className="h-9 gap-1.5 rounded-xl text-xs" onClick={() => downloadReport(detail.id, detail.filename)} data-testid="attr-download-btn">
                      <Download className="h-3.5 w-3.5" /> Fichier source
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" className="h-9 w-9 rounded-xl p-0 text-rose-500 hover:text-rose-600" data-testid="attr-delete-btn"><Trash2 className="h-4 w-4" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent data-testid="attr-delete-dialog">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Supprimer ce rapport ?</AlertDialogTitle>
                          <AlertDialogDescription>Le rapport du {fmtReportDate(detail.report_date)} et son fichier source seront définitivement supprimés.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annuler</AlertDialogCancel>
                          <AlertDialogAction onClick={() => removeReport(detail.id)} className="bg-rose-600 hover:bg-rose-700" data-testid="attr-delete-confirm">Supprimer</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                )}
              </div>
              <div className="min-w-[220px] flex-1 sm:max-w-xs"><UploadZone onFile={upload} busy={busy} compact /></div>
            </div>

            {(detailQ.isLoading || !detail) ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{[0, 1, 2, 3, 4, 5, 6, 7].map((i) => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
            ) : (
              <Tabs defaultValue="synthese" className="space-y-4">
                <TabsList className="flex w-full flex-wrap justify-start gap-1" data-testid="attr-tabs">
                  <TabsTrigger value="synthese" data-testid="attr-tab-synthese"><Activity className="mr-1.5 h-3.5 w-3.5" /> Synthèse</TabsTrigger>
                  <TabsTrigger value="rentabilite" data-testid="attr-tab-rentabilite"><Coins className="mr-1.5 h-3.5 w-3.5" /> Rentabilité</TabsTrigger>
                  <TabsTrigger value="qualite" data-testid="attr-tab-qualite"><ShieldAlert className="mr-1.5 h-3.5 w-3.5" /> Qualité</TabsTrigger>
                  <TabsTrigger value="segments" data-testid="attr-tab-segments"><Layers className="mr-1.5 h-3.5 w-3.5" /> Segments</TabsTrigger>
                  <TabsTrigger value="recruteurs" data-testid="attr-tab-recruteurs"><UserMinus className="mr-1.5 h-3.5 w-3.5" /> Recruteurs</TabsTrigger>
                  {isAmnesty && (
                    <TabsTrigger value="remboursements" data-testid="attr-tab-remboursements"><Receipt className="mr-1.5 h-3.5 w-3.5" /> Remboursements</TabsTrigger>
                  )}
                </TabsList>

                {/* ============ SYNTHÈSE ============ */}
                <TabsContent value="synthese" className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <StatCard icon={HeartPulse} label="Taux d'attrition" accent={C_ARRET} value={fmtPct(m.taux_attrition)}
                      sub={`${fmtInt(m.donateurs_arretes)} arrêts / ${fmtInt(m.total_mandats)} mandats`} delta={attritionDelta} deltaGoodWhenDown testid="attr-kpi-attrition" />
                    <StatCard icon={AlertTriangle} label="Attrition précoce" accent={C_SUSPENDU} value={fmtPct(m.taux_attrition_precoce)}
                      sub={`${fmtInt(m.arretes_precoces)} arrêts à ≤ 3 échéances`} testid="attr-kpi-precoce" />
                    <StatCard icon={Users} label="Donateurs actifs" accent={C_ACTIF} value={fmtInt(m.donateurs_actifs)}
                      sub={`${fmtPct(m.taux_actifs)} du portefeuille`} delta={actifsDelta} testid="attr-kpi-actifs" />
                    <StatCard icon={Wallet} label="MRR actif" accent="var(--tenant-accent-600)" value={fmtEUR(m.mrr_actif)}
                      sub={`${fmtEUR(m.montant_moyen_actif)} / donateur`} testid="attr-kpi-mrr" />
                    <StatCard icon={Coins} label="Revenu réel / donateur" accent="var(--tenant-accent-600)" value={fmtEUR(m.revenu_moyen_donateur)}
                      sub={`${fmt1(m.duree_vie_moyenne)} prélèvements en moyenne`} testid="attr-kpi-revenu" />
                    <StatCard icon={TrendingDown} label="Montant perdu / mois" accent={C_ARRET} value={fmtEUR(m.montant_perdu)} sub="dons arrêtés" testid="attr-kpi-perdu" />
                    <StatCard icon={UserMinus} label="Recruteurs" accent={riskCount > 0 ? C_ARRET : 'var(--tenant-accent-600)'}
                      value={fmtInt(m.nb_recruteurs)} sub={riskCount > 0 ? `${riskCount} à risque à traiter` : 'aucun au-dessus du seuil'} testid="attr-kpi-recruteurs" />
                    <StatCard icon={Percent} label="Âge moyen" accent="var(--tenant-accent-600)" value={`${fmt1(m.age_moyen)} ans`} sub="des donateurs" testid="attr-kpi-age" />
                  </div>

                  {(detail.warnings || []).length > 0 && (
                    <div className="flex items-start gap-2 rounded-xl border border-amber-300/60 bg-amber-50 p-3 text-xs text-amber-800" data-testid="attr-warnings">
                      <Info className="mt-0.5 h-4 w-4 shrink-0" /><div>{detail.warnings.join(' · ')}</div>
                    </div>
                  )}

                  <div className="grid gap-4 lg:grid-cols-3">
                    <Card className="p-5 lg:col-span-2" data-testid="attr-evolution-card">
                      <SectionTitle icon={Activity} title="Évolution du taux d'attrition" desc="Attrition globale vs attrition précoce (≤ 3 échéances), par rapport."
                        right={<span className="text-xs text-muted-foreground">{evolution.length} rapport{evolution.length > 1 ? 's' : ''}</span>} />
                      <TrendAreaChart data={evoData} height={250} xKey="period" xTickFormatter={fmtMonthShort} formatY={(v) => `${v}%`} formatTip={(v) => `${v} %`}
                        series={[{ key: 'attrition', name: "Taux d'attrition", color: C_ARRET }, { key: 'precoce', name: 'Attrition précoce', color: C_SUSPENDU }]} />
                    </Card>
                    <Card className="p-5" data-testid="attr-status-card">
                      <SectionTitle title="Répartition du portefeuille" desc={`Au ${fmtReportDate(detail.report_date)}`} />
                      <SegBar testid="attr-status-bar" segments={[
                        { k: 'Actifs', v: m.donateurs_actifs, c: C_ACTIF },
                        { k: 'Arrêtés', v: m.donateurs_arretes, c: C_ARRET },
                        { k: 'Suspendus', v: m.donateurs_suspendus, c: C_SUSPENDU },
                      ]} />
                      <div className="mt-4 flex items-center justify-between rounded-lg accent-bg-soft p-3">
                        <span className="text-xs font-medium text-muted-foreground">Total mandats</span>
                        <span className="font-display text-lg font-bold">{fmtInt(m.total_mandats)}</span>
                      </div>
                    </Card>
                  </div>
                </TabsContent>

                {/* ============ RENTABILITÉ ============ */}
                <TabsContent value="rentabilite" className="space-y-4">
                  <Card className="p-5" data-testid="attr-ltv-card">
                    <SectionTitle icon={Coins} title="Rentabilité du recrutement (LTV)"
                      desc="Ce que rapporte réellement un donateur (prélèvements payés × montant) comparé à son coût d'acquisition." />
                    <div className="grid gap-4 lg:grid-cols-3">
                      <div className="rounded-2xl border p-4">
                        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Revenu réel moyen / donateur</div>
                        <div className="mt-1 font-display text-3xl font-bold accent-text">{fmtEUR(revMoy)}</div>
                        <div className="mt-1 text-xs text-muted-foreground">soit {fmt1(m.duree_vie_moyenne)} prélèvements × {fmtEUR(montantMoy)} en moyenne</div>
                        <div className="mt-2 text-xs text-muted-foreground">Total encaissé (cohorte) : <strong className="text-foreground">{fmtEUR(m.revenu_encaisse_total)}</strong></div>
                      </div>
                      <div className="rounded-2xl border p-4">
                        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground"><Target className="h-3.5 w-3.5" /> Coût d'acquisition / donateur</div>
                        <div className="mt-2 flex items-center gap-2">
                          <Input type="number" inputMode="decimal" placeholder="ex. 80" value={cpa} onChange={(e) => setCpa(e.target.value)} className="h-10 w-28 text-lg font-semibold" data-testid="attr-cpa-input" />
                          <span className="text-sm text-muted-foreground">€</span>
                        </div>
                        <div className="mt-2 text-[11px] text-muted-foreground">Saisissez votre coût moyen pour recruter un donateur (coût agence / terrain).</div>
                      </div>
                      <div className={`rounded-2xl border p-4 ${cpaNum > 0 ? '' : 'opacity-60'}`} data-testid="attr-breakeven">
                        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Verdict rentabilité</div>
                        {cpaNum > 0 ? (
                          <>
                            <div className="mt-1 font-display text-3xl font-bold" style={{ color: revMoy >= cpaNum ? C_ACTIF : C_ARRET }}>
                              {revMoy >= cpaNum ? 'Rentable' : 'À perte'}
                            </div>
                            <div className="mt-1 text-xs text-muted-foreground">
                              Récupère <strong className="text-foreground">{fmtPct(roiRatio * 100)}</strong> du coût · seuil ≈ <strong className="text-foreground">{breakevenEch}</strong> prélèvements
                            </div>
                            <div className="mt-1 text-xs text-muted-foreground">≈ <strong className="text-foreground">{fmtPct(pctProfitable)}</strong> des donateurs atteignent ce seuil</div>
                          </>
                        ) : (
                          <div className="mt-2 text-sm text-muted-foreground">Renseignez le coût d'acquisition pour voir le seuil de rentabilité et le ROI.</div>
                        )}
                      </div>
                    </div>
                  </Card>

                  <Card className="p-5" data-testid="attr-montant-card">
                    <SectionTitle icon={Wallet} title="Attrition par montant de don" desc="Pousser des montants élevés au recrutement dégrade-t-il la rétention ?" />
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tranche</TableHead>
                          <TableHead className="text-right">Donateurs</TableHead>
                          <TableHead className="w-[200px]">Taux d'attrition</TableHead>
                          <TableHead className="text-right">Éch. moy.</TableHead>
                          <TableHead className="text-right">Revenu réel moy.</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(detail.montant_buckets || []).map((b) => (
                          <TableRow key={b.bucket} data-testid={`attr-montant-${b.bucket}`}>
                            <TableCell className="font-medium">{b.bucket}</TableCell>
                            <TableCell className="text-right">{fmtInt(b.total)}</TableCell>
                            <TableCell><RateBar taux={b.taux_attrition} tone={rateTone(b.taux_attrition)} /></TableCell>
                            <TableCell className="text-right">{fmt1(b.echeance_moy)}</TableCell>
                            <TableCell className="text-right font-medium">{fmtEUR(b.revenu_moy)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Card>
                </TabsContent>

                {/* ============ QUALITÉ ============ */}
                <TabsContent value="qualite" className="space-y-4">
                  <div className="grid gap-4 lg:grid-cols-2">
                    <Card className="p-5" data-testid="attr-rejets-card">
                      <SectionTitle icon={ShieldAlert} title="Nature des arrêts" desc="Rejets bancaires (techniques) vs arrêts volontaires vs fraude — remédiations différentes." />
                      <SegBar testid="attr-rejets-bar" segments={[
                        { k: 'Technique (banque)', v: rej.technique, c: C_ARRET },
                        { k: 'Volontaire', v: rej.volontaire, c: C_SUSPENDU },
                        { k: 'Fraude', v: rej.fraude, c: C_DARK },
                      ]} />
                      {(rej.top_technique || []).length > 0 && (
                        <div className="mt-4">
                          <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Principaux rejets bancaires</div>
                          <div className="space-y-1">
                            {rej.top_technique.map((t) => (
                              <div key={t.label} className="flex items-center justify-between rounded-md bg-muted/40 px-2 py-1 text-xs">
                                <span className="truncate">{t.label}</span><span className="ml-2 shrink-0 font-semibold">{fmtInt(t.count)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </Card>

                    <Card className="p-5" data-testid="attr-survival-card">
                      <SectionTitle icon={Activity} title="Courbe de survie" desc="Part des donateurs encore présents après N prélèvements — révèle où se situe la falaise." />
                      <TrendAreaChart data={survData} height={250} xKey="period" formatY={(v) => `${v}%`} formatTip={(v) => `${v} %`}
                        series={[{ key: 'retention', name: 'Rétention', color: 'var(--tenant-accent-600)' }]} />
                      <div className="mt-1 text-center text-[11px] text-muted-foreground">Nombre de prélèvements payés</div>
                    </Card>
                  </div>

                  <div className="grid gap-4 lg:grid-cols-2">
                    <Card className="p-5" data-testid="attr-echeance-card">
                      <SectionTitle title="Attrition précoce" desc="Nombre d'échéances payées par les donateurs arrêtés (0–3 = problème de qualité au recrutement)." />
                      <LabeledBars data={echData} height={240} xKey="period" color={C_ARRET} highlightLast={false}
                        formatLabel={(v) => (v ? fmtInt(v) : '')} formatTip={(v) => `${fmtInt(v)} donateurs`} />
                      <div className="mt-1 text-center text-[11px] text-muted-foreground">Échéances payées avant l'arrêt</div>
                    </Card>
                    <Card className="p-5" data-testid="attr-motifs-card">
                      <SectionTitle title="Motifs d'arrêt" desc="Principales raisons déclarées des arrêts." />
                      {motifData.length ? (
                        <HorizontalBars data={motifData} height={260} color={C_ARRET} yWidth={168}
                          formatTip={(v, n, item) => `${fmtInt(v)} arrêts · ${item?.payload?.pct ?? 0} %`} />
                      ) : <p className="py-10 text-center text-sm text-muted-foreground">Aucun motif renseigné.</p>}
                    </Card>
                  </div>
                </TabsContent>

                {/* ============ SEGMENTS ============ */}
                <TabsContent value="segments" className="space-y-4">
                  <div className="grid gap-4 lg:grid-cols-2">
                    <Card className="p-5" data-testid="attr-canal-card">
                      <SectionTitle icon={Layers} title="Attrition par canal" desc="Rue, Rue itinérant, Sites privés — rattaché via les données de recrutement." />
                      <Table>
                        <TableHeader><TableRow><TableHead>Canal</TableHead><TableHead className="text-right">Donateurs</TableHead><TableHead className="w-[180px]">Attrition</TableHead><TableHead className="text-right">Revenu moy.</TableHead></TableRow></TableHeader>
                        <TableBody>
                          {(detail.by_canal || []).map((c) => (
                            <TableRow key={c.canal} data-testid={`attr-canal-${c.canal}`}>
                              <TableCell className="font-medium">{c.canal}</TableCell>
                              <TableCell className="text-right">{fmtInt(c.total)}</TableCell>
                              <TableCell><RateBar taux={c.taux_attrition} tone={rateTone(c.taux_attrition)} /></TableCell>
                              <TableCell className="text-right font-medium">{fmtEUR(c.revenu_moy)}</TableCell>
                            </TableRow>
                          ))}
                          {!(detail.by_canal || []).length && <TableRow><TableCell colSpan={4} className="py-6 text-center text-sm text-muted-foreground">Canal non disponible pour ce rapport.</TableCell></TableRow>}
                        </TableBody>
                      </Table>
                    </Card>
                    <Card className="p-5" data-testid="attr-region-card">
                      <SectionTitle icon={MapPin} title="Attrition par région" />
                      <div className="max-h-[320px] overflow-y-auto">
                        <Table>
                          <TableHeader><TableRow><TableHead>Région</TableHead><TableHead className="text-right">Donateurs</TableHead><TableHead className="w-[160px]">Attrition</TableHead></TableRow></TableHeader>
                          <TableBody>
                            {(detail.by_region || []).map((c) => (
                              <TableRow key={c.region} data-testid={`attr-region-${c.region}`}>
                                <TableCell className="font-medium">{c.region}</TableCell>
                                <TableCell className="text-right">{fmtInt(c.total)}</TableCell>
                                <TableCell><RateBar taux={c.taux_attrition} tone={rateTone(c.taux_attrition)} /></TableCell>
                              </TableRow>
                            ))}
                            {!(detail.by_region || []).length && <TableRow><TableCell colSpan={3} className="py-6 text-center text-sm text-muted-foreground">Région non disponible.</TableCell></TableRow>}
                          </TableBody>
                        </Table>
                      </div>
                    </Card>
                  </div>

                  <Card className="p-5" data-testid="attr-campagne-card">
                    <SectionTitle icon={Megaphone} title="Attrition par campagne" desc="Repérer les campagnes/missions qui recrutent des donateurs peu fidèles." />
                    <div className="max-h-[420px] overflow-y-auto rounded-xl border">
                      <Table>
                        <TableHeader className="sticky top-0 z-10 bg-card">
                          <TableRow><TableHead>Campagne</TableHead><TableHead>Canal</TableHead><TableHead>Région</TableHead><TableHead className="text-right">Donateurs</TableHead><TableHead className="w-[170px]">Attrition</TableHead><TableHead className="text-right">Revenu moy.</TableHead></TableRow>
                        </TableHeader>
                        <TableBody>
                          {(detail.by_campagne || []).map((c) => (
                            <TableRow key={c.code} data-testid={`attr-campagne-${c.code}`}>
                              <TableCell className="font-mono text-xs font-semibold">{c.code}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">{c.canal || '—'}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">{c.region || '—'}</TableCell>
                              <TableCell className="text-right">{fmtInt(c.total)}</TableCell>
                              <TableCell><RateBar taux={c.taux_attrition} tone={rateTone(c.taux_attrition)} /></TableCell>
                              <TableCell className="text-right font-medium">{fmtEUR(c.revenu_moy)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </Card>
                </TabsContent>

                {/* ============ RECRUTEURS ============ */}
                <TabsContent value="recruteurs" className="space-y-4">
                  {(() => {
                    const risk = detail.recruteurs_a_risque || [];
                    const cfg = detail.risk_config || { taux_eleve: 60, min_volume: 15 };
                    const impact = risk.reduce((s, r) => s + (r.montant_perdu_est || 0), 0);
                    if (risk.length === 0) {
                      return (
                        <Card className="flex items-center gap-3 border-emerald-200 bg-emerald-50/50 p-4" data-testid="attr-risk-card">
                          <span className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: `${C_ACTIF}1A`, color: C_ACTIF }}><UserMinus className="h-4.5 w-4.5" /></span>
                          <div>
                            <div className="font-display text-sm font-semibold">Aucun recruteur au-dessus du seuil</div>
                            <div className="text-xs text-muted-foreground">Aucun recruteur ne dépasse {cfg.taux_eleve}% d'attrition sur au moins {cfg.min_volume} recrutements pour ce rapport.</div>
                          </div>
                        </Card>
                      );
                    }
                    return (
                      <Card className="border-rose-200 p-5" data-testid="attr-risk-card">
                        <SectionTitle icon={AlertTriangle} title={<span className="flex items-center gap-2">Recruteurs à risque <Badge className="bg-rose-600 text-[10px] hover:bg-rose-600" data-testid="attr-risk-count">{risk.length}</Badge></span>}
                          desc={`Attrition ≥ ${cfg.taux_eleve}% (ou très au-dessus de la moyenne) sur ≥ ${cfg.min_volume} recrutements — à traiter en priorité.`}
                          right={<span className="text-xs text-muted-foreground">≈ <strong className="text-foreground">{fmtEUR(impact)}</strong> / mois</span>} />
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          {risk.map((r) => (
                            <div key={r.code} className="rounded-xl border border-rose-200 bg-rose-50/40 p-3" data-testid={`attr-risk-${r.code}`}>
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <div className="truncate font-display text-sm font-semibold">{r.nom || 'Nom inconnu'}</div>
                                  <div className="font-mono text-[10px] text-muted-foreground">{r.code}</div>
                                </div>
                                <div className="text-right">
                                  <div className="font-display text-lg font-bold" style={{ color: C_ARRET }}>{fmtPct(r.taux_attrition)}</div>
                                  <div className="text-[10px] text-muted-foreground">attrition</div>
                                </div>
                              </div>
                              <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
                                <span>{fmtInt(r.arretes)}/{fmtInt(r.total)} arrêtés</span>
                                <span className="font-semibold text-foreground">−{fmtEUR(r.montant_perdu_est)}/mois</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </Card>
                    );
                  })()}

                  <Card className="p-5" data-testid="attr-recruteurs-card">
                    <SectionTitle title="Performance par recruteur"
                      desc="Nom, taux d'attrition, échéances moyennes, revenu et impact financier par recruteur."
                      right={(
                        <div className="flex items-center gap-2">
                          <Button variant={onlyRisk ? 'default' : 'outline'} size="sm" className="h-8 gap-1.5 rounded-lg text-[11px]" onClick={() => setOnlyRisk((v) => !v)} data-testid="attr-risk-toggle" style={onlyRisk ? { background: C_ARRET } : undefined}>
                            <AlertTriangle className="h-3.5 w-3.5" /> À risque
                          </Button>
                          <Badge variant="secondary" className="text-[10px]">Top {Math.min(30, (detail.by_recruteur || []).length)} par volume</Badge>
                        </div>
                      )} />
                    <div className="max-h-[460px] overflow-y-auto rounded-xl border">
                      <Table>
                        <TableHeader className="sticky top-0 z-10 bg-card">
                          <TableRow>
                            <TableHead className="min-w-[180px]">Recruteur</TableHead>
                            <TableHead className="text-right">Recrutés</TableHead>
                            <TableHead className="text-right">Arrêtés</TableHead>
                            <TableHead className="w-[160px]">Attrition</TableHead>
                            <TableHead className="text-right">Éch. moy.</TableHead>
                            <TableHead className="text-right">Revenu moy.</TableHead>
                            {cpaNum > 0 && <TableHead className="text-right">Rentable ?</TableHead>}
                            <TableHead className="text-right">Perte €/mois</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(detail.by_recruteur || [])
                            .filter((r) => !onlyRisk || r.risk === 'eleve' || r.risk === 'surveiller')
                            .map((r) => {
                              const tone = r.risk === 'eleve' ? C_ARRET : r.risk === 'surveiller' ? C_SUSPENDU : C_ACTIF;
                              const rentable = cpaNum > 0 ? (r.revenu_moy || 0) >= cpaNum : null;
                              return (
                                <TableRow key={r.code} data-testid={`attr-recruteur-${r.code}`} style={{ boxShadow: `inset 3px 0 0 ${r.risk === 'eleve' ? C_ARRET : r.risk === 'surveiller' ? C_SUSPENDU : 'transparent'}` }}>
                                  <TableCell>
                                    <div className="font-medium leading-tight">{r.nom || 'Nom inconnu'}</div>
                                    <div className="font-mono text-[10px] text-muted-foreground">{r.code}</div>
                                  </TableCell>
                                  <TableCell className="text-right">{fmtInt(r.total)}</TableCell>
                                  <TableCell className="text-right" style={{ color: C_ARRET }}>{fmtInt(r.arretes)}</TableCell>
                                  <TableCell><RateBar taux={r.taux_attrition} tone={tone} /></TableCell>
                                  <TableCell className="text-right">{fmt1(r.echeance_moy_arret)}</TableCell>
                                  <TableCell className="text-right">{fmtEUR(r.revenu_moy)}</TableCell>
                                  {cpaNum > 0 && (
                                    <TableCell className="text-right">
                                      <Badge variant="outline" className="text-[10px]" style={{ color: rentable ? C_ACTIF : C_ARRET, borderColor: rentable ? C_ACTIF : C_ARRET }}>
                                        {rentable ? 'Oui' : 'Non'}
                                      </Badge>
                                    </TableCell>
                                  )}
                                  <TableCell className="text-right text-muted-foreground">{fmtEUR(r.montant_perdu_est)}</TableCell>
                                </TableRow>
                              );
                            })}
                        </TableBody>
                      </Table>
                    </div>
                  </Card>
                </TabsContent>

                {/* ============ REMBOURSEMENTS (Amnesty) ============ */}
                {isAmnesty && (
                  <TabsContent value="remboursements" className="space-y-4">
                    <RemboursementsSection association="AMNESTY" />
                  </TabsContent>
                )}
              </Tabs>
            )}
          </>
        )}

        <div className="flex items-center gap-2 rounded-xl border accent-bg-soft p-3 text-xs text-muted-foreground" data-testid="attr-kpi-hint">
          <ArrowRight className="h-4 w-4 accent-text" />
          <span>Astuce : dans le <strong className="text-foreground">Plan stratégique</strong>, un KPI peut se brancher sur une mesure d'attrition (ex. « Taux d'attrition ») pour afficher automatiquement la valeur du dernier rapport.</span>
        </div>

        <p className="pb-4 text-center text-[11px] text-muted-foreground">
          <ShieldCheck className="mr-1 inline h-3.5 w-3.5" /> Données confidentielles — réservées à l'administration SHARITI.
        </p>
      </div>
    </TooltipProvider>
  );
}
